import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getWhatsAppStatus,
  getWhatsAppConversations,
  getWhatsAppMessages,
  markWhatsAppAsRead,
  sendWhatsAppMessage,
  saveWhatsAppConfig,
  type WhatsAppStatus,
  type WhatsAppConversation,
  type WhatsAppMessage,
} from '../services/whatsapp';
import { WhatsAppIcon, RefreshIcon, XIcon } from './icons/AdminIcons';
import './WhatsAppSection.css';

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

interface WhatsAppSectionProps {
  isDarkMode?: boolean;
}

const WhatsAppSection: React.FC<WhatsAppSectionProps> = ({ isDarkMode }) => {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [configForm, setConfigForm] = useState({ accountSid: '', authToken: '', phoneNumber: '' });
  const [savingConfig, setSavingConfig] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await getWhatsAppStatus();
      setStatus({ connected: data.connected, phoneNumber: data.phoneNumber });
      setError(null);
      return data.connected;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al conectar');
      setStatus({ connected: false, phoneNumber: null });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await getWhatsAppConversations();
      setConversations(data.conversations || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar conversaciones');
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone: string) => {
    try {
      const data = await getWhatsAppMessages(phone);
      setMessages(data.messages || []);
      await markWhatsAppAsRead(phone);
      loadConversations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar mensajes');
    }
  }, [loadConversations]);

  useEffect(() => {
    loadStatus().then((connected) => {
      if (connected) loadConversations();
    });
  }, [loadStatus, loadConversations]);

  useEffect(() => {
    if (!selectedPhone) {
      setMessages([]);
      return;
    }
    loadMessages(selectedPhone);
  }, [selectedPhone]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleRefresh = () => {
    setLoading(true);
    loadStatus().then((connected) => {
      if (connected) {
        loadConversations();
        if (selectedPhone) loadMessages(selectedPhone);
      }
    });
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configForm.accountSid.trim() || !configForm.authToken.trim() || !configForm.phoneNumber.trim()) {
      setError('Completa Account SID, Auth Token y número de WhatsApp.');
      return;
    }
    setSavingConfig(true);
    setError(null);
    try {
      const data = await saveWhatsAppConfig({
        accountSid: configForm.accountSid.trim(),
        authToken: configForm.authToken.trim(),
        phoneNumber: configForm.phoneNumber.trim(),
      });
      setStatus({ connected: true, phoneNumber: data.phoneNumber });
      setConfigForm({ accountSid: '', authToken: '', phoneNumber: '' });
      loadConversations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPhone || !replyText.trim()) return;
    setSending(true);
    setError(null);
    try {
      await sendWhatsAppMessage(selectedPhone, replyText.trim());
      setReplyText('');
      loadMessages(selectedPhone);
      loadConversations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="wa-section wa-section-loading">
        <div className="wa-spinner" />
        <p>Cargando...</p>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="wa-section wa-section-connect">
        <div className="wa-connect-card">
          <WhatsAppIcon width={48} height={48} className="wa-connect-icon" />
          <h2>Conectar WhatsApp (Twilio)</h2>
          <p>Vincula tu número de Twilio para recibir, leer y responder mensajes desde el panel.</p>
          <p className="wa-connect-hint">Necesitas Account SID, Auth Token y el número de WhatsApp de Twilio (ej. +14155238886 para sandbox).</p>
          {error && <p className="wa-error">{error}</p>}
          <form onSubmit={handleSaveConfig} className="wa-config-form">
            <div className="wa-form-row">
              <label>Account SID</label>
              <input
                type="text"
                value={configForm.accountSid}
                onChange={(e) => setConfigForm((f) => ({ ...f, accountSid: e.target.value }))}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                autoComplete="off"
              />
            </div>
            <div className="wa-form-row">
              <label>Auth Token</label>
              <input
                type="password"
                value={configForm.authToken}
                onChange={(e) => setConfigForm((f) => ({ ...f, authToken: e.target.value }))}
                placeholder="••••••••••••••••••••••••••••••••"
                autoComplete="off"
              />
            </div>
            <div className="wa-form-row">
              <label>Número WhatsApp (Twilio)</label>
              <input
                type="text"
                value={configForm.phoneNumber}
                onChange={(e) => setConfigForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                placeholder="+14155238886"
              />
            </div>
            <button type="submit" className="wa-btn wa-btn-primary" disabled={savingConfig}>
              {savingConfig ? 'Guardando...' : 'Conectar'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`wa-section ${isDarkMode ? 'wa-section-dark' : ''}`}>
      <div className="wa-header">
        <div>
          <h2><WhatsAppIcon width={24} height={24} /> WhatsApp</h2>
          <p className="wa-phone">{status.phoneNumber || 'Número conectado'}</p>
        </div>
        <div className="wa-header-actions">
          <button type="button" className="wa-btn wa-btn-icon" onClick={handleRefresh} title="Actualizar">
            <RefreshIcon width={18} height={18} />
          </button>
        </div>
      </div>

      {error && (
        <div className="wa-toast wa-toast-error">
          {error}
          <button type="button" aria-label="Cerrar" onClick={() => setError(null)}><XIcon width={16} height={16} /></button>
        </div>
      )}

      <div className="wa-layout">
        <div className="wa-list">
          <div className="wa-list-toolbar">
            Conversaciones
            {listLoading && <span className="wa-loading-dots"> Cargando...</span>}
          </div>
          <ul className="wa-conversation-list">
            {conversations.length === 0 && !listLoading && (
              <li className="wa-empty-list">No hay conversaciones aún.</li>
            )}
            {conversations.map((c) => (
              <li key={c.phone}>
                <button
                  type="button"
                  className={`wa-conversation-item ${selectedPhone === c.phone ? 'selected' : ''} ${c.unreadCount > 0 ? 'unread' : ''}`}
                  onClick={() => setSelectedPhone(c.phone)}
                >
                  <span className="wa-conv-phone">{c.phone}</span>
                  <span className="wa-conv-preview">{c.lastBody || '—'}</span>
                  <span className="wa-conv-meta">
                    {formatTime(c.lastAt)}
                    {c.unreadCount > 0 && <span className="wa-conv-badge">{c.unreadCount}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="wa-detail">
          {selectedPhone ? (
            <>
              <div className="wa-detail-header">
                <h3>{selectedPhone}</h3>
              </div>
              <div className="wa-messages-container">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`wa-message-bubble ${m.direction === 'outbound' ? 'outbound' : 'inbound'}`}
                  >
                    <div className="wa-message-body">{m.body}</div>
                    <div className="wa-message-time">{formatTime(m.createdAt)}</div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={handleSendReply} className="wa-reply-form">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  rows={2}
                  disabled={sending}
                />
                <button type="submit" className="wa-btn wa-btn-primary" disabled={sending || !replyText.trim()}>
                  {sending ? 'Enviando...' : 'Enviar'}
                </button>
              </form>
            </>
          ) : (
            <div className="wa-detail-empty">
              Selecciona una conversación o espera a que lleguen mensajes (configura el webhook de Twilio en tu cuenta).
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSection;
